import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { guardarFechaCobroPipeline } from "../../utils/guardarFechaCobroPipeline";
import { resolverElementoHardwarePorClave } from "../../utils/lineasFacturacion/resolverLinea";
import { client } from "../../utils/supabase";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Modal para overridear manualmente la fecha de cobro estimada de una entry del Backlog. Soporta
// tres orígenes (sales / contratos / carteleria hardware) — la lógica de escritura vive en el util
// standalone src/utils/guardarFechaCobroPipeline.js (Fase 1 de "Futuros: reagendar", 2026-09-23),
// reusado tal cual por el despachador `reagendarEntryFuturo` del panel de Futuros (10.1).

// Default del tramo de hardware = facturafecha + 30 (espejo de DIAS_COBRO_HARDWARE_CAPEX en
// pipelineCobroSII.js — misma regla; el pipeline es la fuente de verdad del default, acá solo se
// muestra como referencia "fecha automática").
const DIAS_COBRO_HARDWARE_CAPEX = 30;

const addDias = (fechaStr, dias) => {
  if (!fechaStr) return null;
  const d = new Date(`${String(fechaStr).substring(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().split("T")[0];
};

const parseDetalle = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v !== "string") return null;
  try {
    const p = JSON.parse(v || "[]");
    return Array.isArray(p) ? p : null;
  } catch {
    return null;
  }
};

const formatFechaLarga = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [yyyy, mm, dd] = f.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const formatMonto = (entry) => {
  const n = Number(entry?.importe || 0);
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  return entry?.moneda_dolar ? `US$ ${num}` : num;
};

const EditarFechaCobroModal = ({ show, onHide, entry, pais, onSaved }) => {
  const { id: userId } = useAuthContext();
  // Tramo de hardware de cartelería: origen "carteleria" + linea_id (L6 tramo 3 — lo resuelve
  // pipelineCobroSII por clave natural). Sin linea_id (tramo sin fecha propia → sin línea derivada)
  // no se puede editar por este modal.
  const esHardwareCarteleria = entry?.origen === "carteleria" && entry?.linea_id != null && entry?.negocio_id != null;

  const [fecha, setFecha] = useState(entry?.fecha_override_actual || "");
  const [guardando, setGuardando] = useState(false);
  // El pipeline no desglosa la fecha de factura ni el override crudo del tramo → se leen al abrir,
  // para precargar el input y calcular la "fecha automática" (facturafecha + 30).
  const [hwFacturaFecha, setHwFacturaFecha] = useState(null);
  const [hwOverrideActual, setHwOverrideActual] = useState(null);

  useEffect(() => {
    if (!show || !esHardwareCarteleria) return undefined;
    let vivo = true;
    (async () => {
      // La línea (facturafecha) sale de `lineas_facturacion` por id — sin tocar el array.
      const { data: linea, error: errLinea } = await client
        .from("lineas_facturacion")
        .select("ano, mes, tramo, facturafecha")
        .eq("id", entry.linea_id)
        .single();
      if (!vivo) return;
      if (errLinea) {
        toast.error(`No se pudo leer la línea del tramo de hardware: ${errLinea.message}`);
        return;
      }
      setHwFacturaFecha(linea?.facturafecha || null);
      // `fecha_cobro_estimada` NO vive en `lineas_facturacion` (es una key propia del array, ajena a
      // L1) — se lee del array por CONTENIDO, re-derivando la clave natural de cada elemento (10.1 —
      // nunca por índice recibido de afuera). Read-only: no escribe ni asigna por índice.
      const { data: negocio, error: errNeg } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", entry.negocio_id)
        .single();
      if (!vivo) return;
      if (errNeg) {
        toast.error(`No se pudo leer el tramo de hardware: ${errNeg.message}`);
        return;
      }
      const detalle = parseDetalle(negocio?.detalle_pagos_hardware) || [];
      const tramo = resolverElementoHardwarePorClave(detalle, linea);
      const override = tramo?.fecha_cobro_estimada || null;
      setHwOverrideActual(override);
      setFecha(override || "");
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!entry) return null;

  const esContrato = entry.origen === "contratos";
  const fechaAutomatica = esHardwareCarteleria
    ? addDias(hwFacturaFecha, DIAS_COBRO_HARDWARE_CAPEX)
    : entry.fecha_automatica;
  const tieneOverride = esHardwareCarteleria ? hwOverrideActual != null : entry.tiene_override;

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("Seleccioná una fecha");
      return;
    }
    setGuardando(true);
    try {
      await guardarFechaCobroPipeline({
        f: entry,
        nuevaFecha: fecha,
        userId,
        modo: MODOS_VALIDACION.ESTRICTO,
        pais,
      });
      toast.success("Fecha de cobro actualizada");
      // Patch puntual (Fase 2A): el padre reubica la fila del Backlog sin refetch. Pasa la fecha
      // efectiva nueva + el override para que reconstruya el item de `pipeline` por su id.
      onSaved?.({ fecha_estimada: fecha, fecha_override_actual: fecha, tiene_override: true });
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleVolverAutomatica = async () => {
    setGuardando(true);
    try {
      await guardarFechaCobroPipeline({ f: entry, nuevaFecha: null, userId });
      toast.success("Restaurada a fecha automática");
      // Revert: la fecha efectiva vuelve a la automática (el modal la conoce en todos los orígenes,
      // incluido hardware de cartelería que la deriva de facturafecha+30). override = false.
      onSaved?.({ fecha_estimada: fechaAutomatica, fecha_override_actual: null, tiene_override: false });
    } catch (e) {
      toast.error(`Error al restaurar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Editar fecha de cobro estimada</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Cliente:</strong> {entry.cliente || "—"}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
          {esContrato && (
            <div>
              <strong>Cuota:</strong> {entry.cuota_index + 1}
            </div>
          )}
          <div>
            <strong>Fecha automática:</strong> {formatFechaLarga(fechaAutomatica)}
            <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>(sin override)</span>
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de cobro override</Form.Label>
          <SelectorFechaDocumento
            value={fecha}
            onChange={(nuevaFecha) => setFecha(nuevaFecha || "")}
            pais={pais}
            modo={MODOS_VALIDACION.ESTRICTO}
            disabled={guardando}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div>
          {tieneOverride && (
            <Button variant="outline-secondary" size="sm" onClick={handleVolverAutomatica} disabled={guardando}>
              Volver a automática
            </Button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
            Cerrar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditarFechaCobroModal;
